// Fork-lineage reads. patch_forks records "this patch is a fork of
// that patch"; these helpers walk it in both directions via direct
// anon Supabase reads (RLS shows a lineage row only when the source
// patch is public/unlisted, and the !inner embeds drop rows whose
// patch the viewer can't see — private forks simply don't appear).
//
// Both directions embed the linked patch's code so a lineage click can
// load-and-play through the same path as a feed row, with no second
// fetch.

import { getSupabase } from '../auth/client';

export type LineagePatch = {
  patchId: string;
  title: string;
  code: string;
  authorHandle: string;
};

export type LineageRow = {
  patch?: {
    id?: string | null;
    title?: string | null;
    code?: string | null;
    profiles?: { handle?: string | null; display_name?: string | null } | null;
  } | null;
};

// The two FK-disambiguated embeds (patch_forks has two FKs into
// patches). !inner so an RLS-hidden patch drops the whole row instead
// of yielding a null embed.
const PARENT_SELECT =
  'patch:patches!patch_forks_source_patch_id_fkey!inner(id, title, code, profiles(handle, display_name))';
const FORK_SELECT =
  'forked_at, patch:patches!patch_forks_fork_patch_id_fkey!inner(id, title, code, profiles(handle, display_name))';

export function mapLineageRow(row: LineageRow): LineagePatch | null {
  const p = row.patch;
  if (!p?.id || typeof p.title !== 'string' || typeof p.code !== 'string') {
    return null;
  }
  return {
    patchId: p.id,
    title: p.title,
    code: p.code,
    authorHandle: p.profiles?.handle ?? p.profiles?.display_name ?? 'anon',
  };
}

/** The patch this one was forked from, or null when it isn't a fork
 *  (or the source isn't visible to the viewer). */
export async function getParent(patchId: string): Promise<LineagePatch | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('patch_forks')
    .select(PARENT_SELECT)
    .eq('fork_patch_id', patchId)
    .maybeSingle();
  if (error) throw new Error(`lineage parent fetch failed: ${error.message}`);
  if (!data) return null;
  return mapLineageRow(data as LineageRow);
}

/** Visible forks of a patch, newest first. Forks are born private, so
 *  this typically shows the subset their owners later published. */
export async function listForks(
  patchId: string,
  limit = 10,
): Promise<LineagePatch[]> {
  const supabase = getSupabase();
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('patch_forks')
    .select(FORK_SELECT)
    .eq('source_patch_id', patchId)
    .order('forked_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`lineage forks fetch failed: ${error.message}`);
  return ((data ?? []) as LineageRow[])
    .map(mapLineageRow)
    .filter((p): p is LineagePatch => p !== null);
}

/** Ancestor chain, root first (oldest ancestor at index 0). Walks
 *  getParent up to `maxHops` links; a repeated id (corrupt/looped
 *  lineage) stops the walk. */
export async function getParentChain(
  patchId: string,
  maxHops = 5,
): Promise<LineagePatch[]> {
  const chain: LineagePatch[] = [];
  const seen = new Set<string>([patchId]);
  let current = patchId;
  for (let hop = 0; hop < maxHops; hop++) {
    const parent = await getParent(current);
    if (!parent || seen.has(parent.patchId)) break;
    chain.unshift(parent);
    seen.add(parent.patchId);
    current = parent.patchId;
  }
  return chain;
}
